<template>
  <div class="search-container" :class="{ 'collapsed': isCollapsed }">
    <div class="search-input-wrapper">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input 
        v-if="!isCollapsed"
        type="text" 
        :value="searchQuery"
        @input="onSearchInput"
        placeholder="Search menu..." 
        class="search-input"
        @focus="onSearchFocus"
        @blur="$emit('hide-history')"
      />
      <button 
        v-if="searchQuery && !isCollapsed" 
        @click="$emit('clear-search')" 
        class="clear-search"
        aria-label="Clear search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
    
    <!-- Search history dropdown -->
    <div v-if="showSearchHistory && searchHistory.length > 0 && !isCollapsed" class="search-history">
      <div v-for="(query, index) in searchHistory" :key="index" 
            class="search-history-item" @click="selectHistoryItem(query)">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span>{{ query }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBar',
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    },
    searchHistory: {
      type: Array,
      default: () => []
    },
    showSearchHistory: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update-search', 'clear-search', 'show-history', 'hide-history', 'search-focus'],
  methods: {
    onSearchInput(event) {
      this.$emit('update-search', event.target.value);
    },
    onSearchFocus() {
      this.$emit('show-history');
      this.$emit('search-focus');
    },
    selectHistoryItem(query) {
      this.$emit('update-search', query);
      this.$emit('hide-history');
    }
  }
}
</script>

<style scoped>
.search-container {
  margin: 0.5rem 0 1.75rem;
  position: relative;
}

.search-container.collapsed {
  margin: 0.5rem 0;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  transition: all 0.3s ease;
}

.search-container.collapsed .search-input-wrapper {
  padding: 0.6rem;
  justify-content: center;
}

.search-input-wrapper:focus-within {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.15);
}

.search-input {
  background: transparent;
  border: none;
  color: white;
  width: 100%;
  padding: 0.3rem 0.5rem;
  font-size: 0.9rem;
  outline: none;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-icon {
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.4);
}

.clear-search {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-search:hover {
  color: white;
}

.clear-search svg {
  width: 16px;
  height: 16px;
}

/* Search history dropdown */
.search-history {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1f2937;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-top: 0.5rem;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.search-history-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.search-history-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.search-history-item svg {
  width: 14px;
  height: 14px;
  margin-right: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

:root[data-theme="light"] .search-input-wrapper {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

:root[data-theme="light"] .search-input-wrapper:focus-within {
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.1);
}

:root[data-theme="light"] .search-input {
  color: #1f2937;
}

:root[data-theme="light"] .search-input::placeholder {
  color: rgba(0, 0, 0, 0.4);
}

:root[data-theme="light"] .search-icon,
:root[data-theme="light"] .clear-search {
  color: rgba(0, 0, 0, 0.4);
}

:root[data-theme="light"] .clear-search:hover {
  color: black;
}

:root[data-theme="light"] .search-history {
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

:root[data-theme="light"] .search-history-item:hover {
  background: rgba(0, 0, 0, 0.03);
}

:root[data-theme="light"] .search-history-item svg {
  color: rgba(0, 0, 0, 0.5);
}
</style> 